// /src/pages/admin/course/CourseSections.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import AdminMenu from '../AdminMenu';
import AdminHeader from '../AdminHeader';
import { ADMIN_API_BASE } from "../../../utils/apiConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faEllipsis,
    faPencil,
    faPlus,
    faReorder,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import CustomSwitch from "../../../components/CustomSwitch";
import ButtonLink from "../../../components/ButtonLink";

const rtlProps = { dir: 'rtl', lang: 'fa' };

const TEXT = {
    pageTitle: (id) => `Course sections · ${id}`,
    loading: 'Loading sections...',
    addSection: 'افزودن سرفصل',
    sectionSettings: 'مشخصات سرفصل',
    labels: {
        title: 'تیتر سرفصل',
        version: 'نسخه',
        description: 'توضیحات سرفصل'
    },
    switch: {
        active: 'فعال',
        inactive: 'غیرفعال'
    },
    contentsHeader: 'محتوای سرفصل',
    addContent: 'افزودن محتوا',
    noContents: 'هنوز هیچ محتوایی برای این سرفصل تعریف نشده است.',
    defaultSectionTitle: 'سرفصل بدون تیتر',
    defaultContentTitle: 'محتوای بدون تیتر',
    tooltips: {
        expand: 'نمایش محتوا',
    },
    buttons: {
        cancel: 'لغو',
        saveNew: 'افزودن سرفصل',
        save: 'ذخیره',
        delete: 'حذف',
    }
};

const Modal = ({ open, title, children, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg w-full max-w-2xl p-6 shadow-lg">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <button onClick={onClose} className="text-gray-600">✖</button>
                </div>
                <div>{children}</div>
            </div>
        </div>
    );
};

const IconButton = ({ children, onClick, title }) => (
    <button onClick={onClick} title={title} className="px-2 py-1 rounded hover:bg-gray-100">
        {children}
    </button>
);

const defaultNewSection = (courseId) => ({
    id: null,
    title: TEXT.defaultSectionTitle,
    position: 0,
    description: '',
    version: 1,
    is_active: true,
    course_id: courseId,
    CourseContents: []
});

export default function CourseSections() {
    const { courseId } = useParams();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [currentSection, setCurrentSection] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [toast, setToast] = useState(null);

    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            credentials: 'include'
        });
        const data = await res.json();
        return data.csrfToken;
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchSections = useCallback(async () => {
        setLoading(true);
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/course/${courseId}/section/`, {
                headers: { 'csrf-token': token },
                withCredentials: true
            });

            const rawSections = res.data.sections || [];
            const normalized = rawSections.map((s) => ({
                ...s,
                CourseContents: (s.CourseContents || []).map((c, cidx) => ({
                    ...c,
                    level: c.level ?? cidx + 1,
                })),
            }));

            setSections(normalized);
        } catch (err) {
            console.error('fetch sections error', err);
            showToast('خطا در بارگذاری سرفصل‌ها', 'error');
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        fetchSections();
    }, [fetchSections]);

    const openEdit = (section) => {
        setCurrentSection(section);
        setOpenEditModal(true);
    };

    const openDelete = (section) => {
        setCurrentSection(section);
        setOpenDeleteModal(true);
    };

    const addSectionLocal = (section) => {
        const maxPos = sections.reduce((m, s) => Math.max(m, s.position || 0), 0);
        const next = {
            ...section,
            position: maxPos + 1,
            CourseContents: section.CourseContents || []
        };
        setSections(prev => [...prev, next]);
    };

    const updateSectionLocal = (updated) => {
        setSections(prev => prev.map(s => (String(s.id) === String(updated.id) ? { ...s, ...updated } : s)));
    };

    const deleteSectionLocal = (id) => {
        setSections(prev => prev.filter(s => String(s.id) !== String(id)));
    };

    // API helpers
    const handleCreateSection = async (sectionPayload) => {
        const token = await getCsrfToken();
        const res = await axios.post(
            `${ADMIN_API_BASE}/course/${courseId}/section/`,
            sectionPayload,
            { headers: { 'csrf-token': token }, withCredentials: true }
        );
        return res.data.section;
    };

    const handleUpdateSection = async (sectionId, sectionPayload) => {
        const token = await getCsrfToken();
        const res = await axios.put(
            `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`,
            sectionPayload,
            { headers: { 'csrf-token': token }, withCredentials: true }
        );
        return res.data.section;
    };

    const createContentsForSection = async (sectionId, contents) => {
        if (!contents || contents.length === 0) return [];
        const token = await getCsrfToken();
        const created = [];

        for (const c of contents) {
            const payload = {
                title: String(c.title || '').trim(),
                type: Number(c.type),
                // level optional – backend can assign next free level safely
            };

            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/`,
                payload,
                { headers: { 'csrf-token': token }, withCredentials: true }
            );
            created.push(res.data.content);
        }

        return created;
    };

    const deleteContentsForSection = async (sectionId, ids) => {
        if (!ids || ids.length === 0) return;
        const token = await getCsrfToken();

        for (const id of ids) {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/${id}`,
                { headers: { 'csrf-token': token }, withCredentials: true }
            );
        }
    };

    const handleDeleteSection = async (sectionId) => {
        try {
            const token = await getCsrfToken();
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`,
                { headers: { 'csrf-token': token }, withCredentials: true }
            );
            deleteSectionLocal(sectionId);
            setOpenDeleteModal(false);
            showToast('سرفصل حذف شد');
        } catch (err) {
            console.error(err);
            showToast('خطا در حذف', 'error');
        }
    };

    // Drag & Drop
    const onDragEnd = async (result) => {
        const { source, destination, type } = result;
        if (!destination) return;

        // SECTION REORDER
        if (type === "SECTION") {
            const newOrder = Array.from(sections);
            const [moved] = newOrder.splice(source.index, 1);
            newOrder.splice(destination.index, 0, moved);

            const positioned = newOrder.map((s, idx) => ({
                ...s,
                position: idx + 1
            }));

            setSections(positioned);

            const payload = positioned.map(s => ({
                id: s.id,
                position: s.position
            }));

            try {
                const token = await getCsrfToken();
                await axios.put(
                    `${ADMIN_API_BASE}/course/${courseId}/section/reorder/`,
                    { sections: payload },
                    { headers: { 'csrf-token': token }, withCredentials: true }
                );
                showToast("ترتیب سرفصل‌ها ذخیره شد");
            } catch (err) {
                console.error("save reorder error", err);
                showToast("خطا در ذخیره ترتیب سرفصل‌ها", "error");
            }

            return;
        }

        // CONTENT REORDER – ONLY existing contents (with id)
        if (type.startsWith("CONTENT-")) {
            const sectionId = type.split("-")[1];
            const secIndex = sections.findIndex((s) => String(s.id) === String(sectionId));
            if (secIndex === -1) return;

            const sec = sections[secIndex];
            const allContents = Array.from(sec.CourseContents || []);

            // only reorder among existing ones
            const existing = allContents.filter(c => c.id && !c._deleted);
            if (source.index >= existing.length || destination.index >= existing.length) return;

            const [moved] = existing.splice(source.index, 1);
            existing.splice(destination.index, 0, moved);

            // assign new levels
            const updatedExisting = existing.map((c, idx) => ({ ...c, level: idx + 1 }));

            // merge back into section (no tmp contents currently in list)
            const newSections = [...sections];
            newSections[secIndex] = { ...sec, CourseContents: updatedExisting };
            setSections(newSections);

            const reorderPayload = updatedExisting.map(c => ({
                id: c.id,
                level: c.level
            }));

            try {
                const token = await getCsrfToken();
                await axios.put(
                    `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/reorder/`,
                    { contents: reorderPayload },
                    { headers: { 'csrf-token': token }, withCredentials: true }
                );
                showToast("ترتیب محتوا ذخیره شد");
            } catch (err) {
                console.error("Error saving content reorder:", err);
                showToast("خطا در ذخیره ترتیب محتوا", "error");
            }
        }
    };

    const toggleExpand = (sectionId) => {
        setExpanded(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    };

    const SectionCard = ({ s, index }) => (
        <Draggable draggableId={String(s.id)} index={index}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className="bg-white rounded-lg p-3 border shadow-sm flex flex-col"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <div
                                    {...provided.dragHandleProps}
                                    className="cursor-grab p-1 rounded hover:bg-gray-100"
                                    title="درگ برای تغییر ترتیب"
                                >
                                    <FontAwesomeIcon icon={faReorder} size={"xl"} />
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold mx-5 text-base">{s.title || 'بدون عنوان'}</h3>
                            </div>
                            <div>
                                <button
                                    onClick={() => toggleExpand(s.id)}
                                    className="text-gray-400 hover:text-black"
                                    title={TEXT.tooltips.expand}
                                >
                                    <FontAwesomeIcon
                                        icon={faChevronLeft}
                                        size={"lg"}
                                        className={expanded[s.id] ? "-rotate-90 transition-all" : "transition-all"}
                                    />
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-5">
                            <ThreeDotMenu
                                onEdit={() => openEdit(s)}
                                onDelete={() => openDelete(s)}
                            />
                        </div>
                    </div>

                    {s.description ? <p className="mt-2 text-sm text-gray-700">{s.description}</p> : null}

                    {expanded[s.id] && (
                        <div className="m-5 bg-gray-200">
                            <Droppable droppableId={`contents-${s.id}`} type={`CONTENT-${s.id}`}>
                                {(providedCont) => (
                                    <div ref={providedCont.innerRef} {...providedCont.droppableProps}
                                         className="space-y-2">
                                        {(s.CourseContents || []).filter(c => !c._deleted).map((c, idx) => (
                                            <Draggable key={String(c.id)} draggableId={String(c.id)} index={idx}>
                                                {(prov) => (
                                                    <div
                                                        ref={prov.innerRef}
                                                        {...prov.draggableProps}
                                                        {...prov.dragHandleProps}
                                                        className="flex items-center justify-between p-2 border rounded bg-white"
                                                    >
                                                        <div>
                                                            <div className="text-sm font-medium">
                                                                {c.title || 'بدون عنوان'}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                level: {c.level} • type: {c.type}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <ButtonLink
                                                                to={`/admin/course/${courseId}/content/${c.id}`}
                                                            >
                                                                <FontAwesomeIcon icon={faChevronLeft} size={"lg"} />
                                                            </ButtonLink>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {providedCont.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    )}
                </div>
            )}
        </Draggable>
    );

    const ThreeDotMenu = ({ onEdit, onDelete }) => {
        const [open, setOpen] = useState(false);
        return (
            <div className="relative inline-block">
                <button onClick={() => setOpen(v => !v)} className="p-3 rounded-lg transition hover:bg-gray-300">
                    <FontAwesomeIcon icon={faEllipsis} size={"xl"} />
                </button>
                {open && (
                    <div className="absolute left-0 mt-2 bg-white border rounded shadow-md w-40 z-40">
                        <button
                            onClick={() => {
                                setOpen(false);
                                onEdit();
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                        >
                            <span className="text-black">
                                <FontAwesomeIcon icon={faPencil} size={"lg"} />
                            </span>
                            <span>ویرایش</span>
                        </button>
                        <button
                            onClick={() => {
                                setOpen(false);
                                onDelete();
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                        >
                            <span className="text-red-600">
                                <FontAwesomeIcon icon={faTrashCan} size={"lg"} />
                            </span>
                            <span>حذف</span>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const SectionEditForm = ({ initial = null, onCancel, onSave }) => {
        const isNew = !initial || !initial.id;
        const [form, setForm] = useState(() => {
            if (!initial) return defaultNewSection(courseId);
            return {
                ...initial,
                CourseContents: initial.CourseContents ? [...initial.CourseContents] : []
            };
        });
        const [activeTab, setActiveTab] = useState('general');
        const [loadingSave, setLoadingSave] = useState(false);

        useEffect(() => {
            if (!initial) {
                setForm(defaultNewSection(courseId));
            } else {
                setForm({
                    ...initial,
                    CourseContents: initial.CourseContents ? [...initial.CourseContents] : []
                });
            }
            setActiveTab('general');
        }, [initial, courseId]);

        const existingContents = (form.CourseContents || []).filter(c => c.id);
        const newContents = (form.CourseContents || []).filter(c => !c.id && !c._deleted);
        const deletedExisting = (form.CourseContents || []).filter(c => c.id && c._deleted);

        const addContent = () => {
            setForm(prev => {
                const list = prev.CourseContents || [];
                const newContent = {
                    id: null,
                    title: TEXT.defaultContentTitle,
                    type: 0,
                    content: {},
                    is_active: true,
                };
                return { ...prev, CourseContents: [...list, newContent] };
            });
        };

        const updateNewContentField = (index, key, value) => {
            setForm(prev => {
                const list = [...(prev.CourseContents || [])];
                const newOnly = list.filter(c => !c.id && !c._deleted);
                const target = newOnly[index];
                if (!target) return prev;

                const globalIndex = list.indexOf(target);
                list[globalIndex] = { ...target, [key]: value };
                return { ...prev, CourseContents: list };
            });
        };

        const removeNewContent = (index) => {
            setForm(prev => {
                const list = [...(prev.CourseContents || [])];
                const newOnly = list.filter(c => !c.id && !c._deleted);
                const target = newOnly[index];
                if (!target) return prev;
                const globalIndex = list.indexOf(target);
                list.splice(globalIndex, 1);
                return { ...prev, CourseContents: list };
            });
        };

        const toggleDeleteExistingContent = (id) => {
            setForm(prev => ({
                ...prev,
                CourseContents: (prev.CourseContents || []).map(c =>
                    c.id === id ? { ...c, _deleted: !c._deleted } : c
                )
            }));
        };

        // STRICT VALIDATION (your choice A)
        const validateForm = () => {
            const title = String(form.title || '').trim();
            if (!title) {
                return 'تیتر سرفصل نمی‌تواند خالی باشد.';
            }

            for (const c of newContents) {
                const t = String(c.title || '').trim();
                if (!t) {
                    return 'همه‌ی محتواهای جدید باید تیتر داشته باشند.';
                }
                const type = Number(c.type);
                if (![0, 1, 2].includes(type)) {
                    return 'نوع محتوا باید 0 (نامشخص)، 1 (درس)، یا 2 (پروژه) باشد.';
                }
            }

            return null;
        };

        const saveSection = async () => {
            const error = validateForm();
            if (error) {
                // we don't have toast here, so bubble error to parent
                throw new Error(error);
            }

            setLoadingSave(true);
            try {
                await onSave({
                    ...form,
                    CourseContents: [...(form.CourseContents || [])],
                });
            } finally {
                setLoadingSave(false);
            }
        };

        return (
            <div className="space-y-6 text-right">
                <div className="flex gap-2 border-b pb-2">
                    {[
                        { id: 'general', label: TEXT.sectionSettings },
                        { id: 'contents', label: TEXT.contentsHeader },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={classNames(
                                'px-4 py-2 rounded-t-md border border-b-0 text-sm',
                                activeTab === tab.id
                                    ? 'bg-white text-black border-gray-300'
                                    : 'bg-gray-100 text-gray-600 border-transparent'
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'general' && (
                    <div className="bg-gray-50 border rounded-lg p-5 space-y-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <h5 className="text-sm font-semibold text-gray-600">{TEXT.sectionSettings}</h5>
                            <CustomSwitch
                                checked={!!form.is_active}
                                onChange={(next) => setForm(prev => ({ ...prev, is_active: next }))}
                                label={form.is_active ? TEXT.switch.active : TEXT.switch.inactive}
                            />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="block text-sm text-gray-600">{TEXT.labels.title}</label>
                                <input
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                    className="w-full border rounded p-2 text-right"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-600">{TEXT.labels.version}</label>
                                <input
                                    type="number"
                                    value={form.version}
                                    onChange={e => setForm({
                                        ...form,
                                        version: parseInt(e.target.value || 1)
                                    })}
                                    className="w-full border rounded p-2 text-right"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600">{TEXT.labels.description}</label>
                            <textarea
                                className="w-full border rounded p-2 text-right"
                                rows="3"
                                value={form.description || ''}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'contents' && (
                    <div className="bg-gray-50 border rounded-lg p-5 space-y-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <h5 className="text-sm font-semibold text-gray-600">{TEXT.contentsHeader}</h5>
                            <button
                                type="button"
                                className="px-4 py-2 border rounded-md text-sm"
                                onClick={addContent}
                            >
                                {TEXT.addContent}
                            </button>
                        </div>

                        {/* Existing contents (read-only, deletable) */}
                        {(existingContents.length > 0 || deletedExisting.length > 0) && (
                            <div className="space-y-2">
                                {existingContents.map((c) => (
                                    <div key={c.id} className="flex justify-between items-center border rounded p-2 bg-white">
                                        <div>
                                            <div className="text-sm font-medium">
                                                {c.title || 'بدون عنوان'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                type: {c.type} • level: {c.level}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <ButtonLink to={`/admin/course/${courseId}/content/${c.id}`}>
                                                <FontAwesomeIcon icon={faPencil} />
                                            </ButtonLink>
                                            <button
                                                type="button"
                                                onClick={() => toggleDeleteExistingContent(c.id)}
                                                className="p-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {deletedExisting.map(c => (
                                    <div key={`deleted-${c.id}`} className="text-xs text-red-600">
                                        برای حذف علامت‌گذاری شد: {c.title}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* New contents (editable, unsaved) */}
                        {newContents.length === 0 ? (
                            <p className="text-sm text-gray-500">{TEXT.noContents}</p>
                        ) : (
                            <div className="space-y-3">
                                {newContents.map((c, idx) => (
                                    <div
                                        key={`new-${idx}`}
                                        className="flex flex-col md:flex-row gap-3 md:items-center border rounded-lg p-3 bg-white"
                                    >
                                        <div className="flex-1">
                                            <label className="block text-xs text-gray-500 mb-1">
                                                {TEXT.labels.title}
                                            </label>
                                            <input
                                                value={c.title || ''}
                                                onChange={e => updateNewContentField(idx, 'title', e.target.value)}
                                                className="w-full border rounded p-2 text-right"
                                            />
                                            <label className="block text-xs text-gray-500 mb-1 mt-2">
                                                نوع محتوا (0: نامشخص، 1: درس، 2: پروژه)
                                            </label>
                                            <input
                                                value={c.type ?? ''}
                                                type="number"
                                                onChange={e => updateNewContentField(idx, 'type', Number(e.target.value))}
                                                className="w-full border rounded p-2 text-right"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => removeNewContent(idx)}
                                                className="p-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                                title={TEXT.buttons.delete}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onCancel} className="px-4 py-2 border rounded">
                        {TEXT.buttons.cancel}
                    </button>
                    <button
                        onClick={async () => {
                            try {
                                await saveSection();
                            } catch (err) {
                                console.error(err);
                                if (err.message) {
                                    // we don't have showToast here, so throw up to parent
                                    alert(err.message); // minimal UX; or you can wire a callback
                                }
                            }
                        }}
                        disabled={loadingSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                    >
                        {loadingSave ? 'در حال ذخیره…' : isNew ? TEXT.buttons.saveNew : TEXT.buttons.save}
                    </button>
                </div>
            </div>
        );
    };

    const onSaveFromEditModal = async (form) => {
        const isNew = !form.id;

        // Extract content ops:
        const allContents = form.CourseContents || [];
        const newContents = allContents.filter(c => !c.id && !c._deleted);
        const deletedExistingIds = allContents.filter(c => c.id && c._deleted).map(c => c.id);

        const sectionPayload = {
            title: form.title,
            description: form.description,
            is_active: form.is_active,
            version: form.version
        };

        try {
            let section;

            if (isNew) {
                section = await handleCreateSection(sectionPayload);
                addSectionLocal({ ...section, CourseContents: [] });
            } else {
                section = await handleUpdateSection(form.id, sectionPayload);
                updateSectionLocal(section);
            }

            const sid = section.id;

            // create new contents
            if (newContents.length > 0) {
                await createContentsForSection(sid, newContents);
            }

            // delete marked existing
            if (deletedExistingIds.length > 0) {
                await deleteContentsForSection(sid, deletedExistingIds);
            }

            // refresh everything to stay fully in sync
            await fetchSections();

            setOpenAddModal(false);
            setOpenEditModal(false);
            showToast('تغییرات ذخیره شد');
        } catch (err) {
            console.error(err);
            showToast(err.message || 'خطا در ذخیره‌سازی', 'error');
        }
    };

    const confirmDelete = async () => {
        if (!currentSection) return;
        await handleDeleteSection(currentSection.id);
    };

    return (
        <div className="flex flex-1" {...rtlProps}>
            <div className="w-1/5">
                <AdminMenu path="./" />
            </div>

            <div className="flex-grow w-4/5">
                <AdminHeader title={`سرفصل‌های دوره ${courseId}`} />
                <div className="w-full px-10 my-8">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex gap-3">
                            <Link to={`/admin/course/${courseId}/section/add`} className="hidden">
                                hidden route
                            </Link>
                            <button
                                onClick={() => setOpenAddModal(true)}
                                className="bg-blue-600 text-white px-5 py-3 gap-2 rounded-md"
                            >
                                <span>{TEXT.addSection}</span>
                                <FontAwesomeIcon icon={faPlus} size={"lg"} />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center text-gray-600">در حال بارگذاری...</div>
                    ) : sections.length === 0 ? (
                        <div className="text-center text-gray-500 py-20">
                            <div className="text-6xl mb-4">📭</div>
                            <div className="text-lg mb-3">هیچ سرفصلی ثبت نشده است.</div>
                        </div>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="all-sections" type="SECTION">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                                        {sections.map((s, idx) => (
                                            <SectionCard key={String(s.id)} s={s} index={idx} />
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                    {toast && (
                        <div
                            className={classNames(
                                'fixed bottom-6 left-6 px-4 py-2 rounded shadow',
                                toast.type === 'success'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-red-600 text-white'
                            )}
                        >
                            {toast.message}
                        </div>
                    )}

                    <Modal open={openAddModal} title="افزودن سرفصل جدید" onClose={() => setOpenAddModal(false)}>
                        <SectionEditForm
                            initial={null}
                            onCancel={() => setOpenAddModal(false)}
                            onSave={onSaveFromEditModal}
                        />
                    </Modal>

                    <Modal open={openEditModal} title="ویرایش سرفصل" onClose={() => setOpenEditModal(false)}>
                        {currentSection && (
                            <SectionEditForm
                                initial={currentSection}
                                onCancel={() => setOpenEditModal(false)}
                                onSave={onSaveFromEditModal}
                            />
                        )}
                    </Modal>

                    <Modal open={openDeleteModal} title="حذف سرفصل" onClose={() => setOpenDeleteModal(false)}>
                        <p>آیا از حذف این سرفصل مطمئن هستید؟ این کار تمام محتواهای داخل آن را حذف می‌کند.</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setOpenDeleteModal(false)}
                                className="px-3 py-1 border rounded"
                            >
                                انصراف
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-3 py-1 bg-red-600 text-white rounded"
                            >
                                حذف
                            </button>
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    );
}
